import { useState } from "react";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuthStore } from "@/features/auth/store/auth-store";
import * as opsApi from "@/features/ops/api";
import { useImpersonationStore } from "@/features/ops/store/impersonation-store";
import type { EntityDetail, OpsUser } from "@/features/ops/types";

const becomeSchema = Yup.object({
  password: Yup.string().required("Confirm your password to continue"),
});

export default function OpsHomePage() {
  const [term, setTerm] = useState("");
  const [users, setUsers] = useState<OpsUser[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [becomeTarget, setBecomeTarget] = useState<OpsUser | null>(null);
  const [entity, setEntity] = useState<EntityDetail | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EntityDetail | null>(null);

  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false);
  const beginImpersonation = useImpersonationStore((s) => s.begin);
  const navigate = useNavigate();

  const search = async () => {
    setLoading(true);
    try {
      const res = await opsApi.searchUsers(term);
      setUsers(res.users);
      setCount(res.meta.count);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const inspectFirm = async (firmId: string) => {
    try {
      setEntity(await opsApi.getEntity(firmId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load firm");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>User directory</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void search();
            }}
          >
            <Input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search all firms by name or email…"
              className="max-w-sm"
            />
            <Button type="submit" disabled={loading}>
              {loading ? "Searching…" : "Search"}
            </Button>
            {count !== null && (
              <span className="text-muted-foreground text-sm">{count} user(s)</span>
            )}
          </form>
        </CardContent>
      </Card>

      {users.length > 0 && (
        <Card className="gap-0 overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Firm</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">
                    {u.name}
                    {!u.isActive && (
                      <Badge variant="secondary" className="ml-2">
                        inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.firm?.name ?? "—"}</TableCell>
                  <TableCell>{u.role ?? "—"}</TableCell>
                  <TableCell className="flex flex-wrap justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setBecomeTarget(u)}>
                      Become
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/admin/ops/users/${u.id}/log`)}
                    >
                      Login log
                    </Button>
                    {u.firm && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => u.firm && inspectFirm(u.firm.id)}
                      >
                        Firm
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Become-user modal (password re-auth) */}
      <Dialog open={!!becomeTarget} onOpenChange={(o) => !o && setBecomeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Become {becomeTarget?.name}</DialogTitle>
            <DialogDescription>
              Confirm your own password to start an audited impersonation session. A banner will let
              you return to yourself.
            </DialogDescription>
          </DialogHeader>
          <Formik
            initialValues={{ password: "" }}
            validationSchema={becomeSchema}
            onSubmit={async (values, { setSubmitting }) => {
              if (!becomeTarget) return;
              try {
                const result = await opsApi.impersonate(becomeTarget.id, values.password);
                beginImpersonation(result);
                setBecomeTarget(null);
                toast.success(`Now viewing as ${result.user.name}`);
                navigate("/dashboard");
              } catch (err) {
                toast.error(err instanceof Error ? err.message : "Could not impersonate");
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <Form className="flex flex-col gap-4">
                <FormField
                  name="password"
                  label="Your password"
                  type="password"
                  autoComplete="current-password"
                />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setBecomeTarget(null)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Starting…" : "Become user"}
                  </Button>
                </DialogFooter>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Entity inspection modal */}
      <Dialog open={!!entity} onOpenChange={(o) => !o && setEntity(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{entity?.name}</DialogTitle>
            <DialogDescription>{entity?.email || "No firm email"}</DialogDescription>
          </DialogHeader>
          {entity && (
            <div className="flex flex-col gap-4">
              <div className="text-muted-foreground grid grid-cols-3 gap-2 text-sm">
                <div>
                  <div className="text-foreground text-lg font-semibold">{entity.userCount}</div>
                  Users
                </div>
                <div>
                  <div className="text-foreground text-lg font-semibold">{entity.clientCount}</div>
                  Clients
                </div>
                <div>
                  <div className="text-foreground text-lg font-semibold">
                    {entity.documentCount}
                  </div>
                  Documents
                </div>
              </div>
              <ul className="text-sm">
                {entity.users.map((u) => (
                  <li key={u.id} className="border-b py-1 last:border-0">
                    {u.name} — {u.email} {u.role ? `(${u.role})` : ""}
                  </li>
                ))}
              </ul>
              {isSuperuser && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    setDeleteTarget(entity);
                    setEntity(null);
                  }}
                >
                  Delete firm and all data…
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete-entity confirm (superuser, type-to-confirm) */}
      <DeleteEntityDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          void search();
        }}
      />
    </div>
  );
}

function DeleteEntityDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: EntityDetail | null;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <AlertDialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {target?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently deletes the firm and all {target?.clientCount} client(s),{" "}
            {target?.documentCount} document(s), and {target?.userCount} user(s). Type{" "}
            <strong>delete</strong> to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="delete"
          aria-label="type delete to confirm"
        />
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setConfirm("")}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={confirm.trim().toLowerCase() !== "delete" || busy}
            onClick={async (e) => {
              e.preventDefault();
              if (!target) return;
              setBusy(true);
              try {
                await opsApi.deleteEntity(target.id, confirm);
                toast.success(`Deleted ${target.name}`);
                setConfirm("");
                onDeleted();
              } catch (err) {
                toast.error(err instanceof Error ? err.message : "Delete failed");
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Deleting…" : "Delete firm"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
